/**
 * Mixpanel analytics integration. When enabled, registers a Mixpanel super property with Optimizely
 * layer decision data. Then sends an event to Mixpanel to track the layer decision.
 * Public API documentation: https://mixpanel.com/help/reference/javascript-full-api-reference
 * @module
 */

var _ = require('optimizely-client-fn');

var JSONEnv = require('core/lib/json');
var Logger = require('core/utils/logger');
var Policy = require('core/utils/policy');
var Poll = require('core/utils/poll');
var RedirectUtil = require('core/utils/redirect');
var trackingPolicies = require('core/constants/tracking_policies');
var urlUtil = require('core/utils/url');
var WindowEnv = require('core/env/window');

// Global variable used for Mixpanel's JS API
var MIXPANEL_VARIABLE = 'mixpanel';
// Limit documented at
// https://mixpanel.com/help/questions/articles/special-or-reserved-properties
var MAXIMUM_PROPERTY_LENGTH = 255;
var VENDOR = 'mixpanel';
var generateAnalyticsValue = require('core/plugins/helpers/generate_analytics_value');

/**
 * @constant
 * @type {string}
 * @private
 */
var HOLDBACK_INITIALS = 'HB';
var HOLDBACK_STRING = 'holdback';
var DEFAULT_STR = '';
var CAMPAIGN_EVENT = 'Campaign Decided: ';
var EXPERIMENT_EVENT = 'Experiment Decided: ';

/**
 * When a layer decision is made:
 *
 * 1. Check if any "mixpanel" settings exist for the layer and bail if not.
 * 2. Verify that variation exists within the experiment
 * 3. If settings and variation exist, poll for the mixpanel object on the window
 * 4. Once object is found, register a Mixpanel super property with the layer information
 * Super property documentation: https://mixpanel.com/help/reference/javascript#super-properties
 *
 * Design doc for this feature: https://confluence.sso.episerver.net/display/EXPENG/Mixpanel+Integration+for+Optimizely+X
 * @param {DecisionData} decisionData
 * @return {Promise} Resolves when tracked with Mixpanel API, or error occurs (in which case the error is logged)
 */
var trackLayerDecisionMixpanel = function(decisionData:DecisionData): Promise<any> {
  var settings = decisionData.integrationSettings;
  if (!settings || !settings[VENDOR]) {
    return;
  }

  if (!decisionData.experimentId || !decisionData.variationId) {
    return;
  }

  var layer:Layer = {
    id: decisionData.layerId,
    name: decisionData.layerName,
    policy: decisionData.layerPolicy,
    integrationStringVersion: decisionData.integrationStringVersion,
    experiments: []
  };

  var isLayerHoldback = decisionData.isLayerHoldback;

  // For A/B tests, we don't want to send any data if the visitor is excluded due to traffic allocation
  if (layer.integrationStringVersion
      && layer.integrationStringVersion === 2
      && Policy.isSingleExperimentPolicy(decisionData.layerPolicy)
      && isLayerHoldback) {
    return;
  }

  var experiment:Experiment = {
    id: decisionData.experimentId,
    name: decisionData.experimentName,
    audienceIds: [],
    variations: []
  };

  var variation:Variation = {
    id: decisionData.variationId,
    name: decisionData.variationName,
    actions: []
  };

  return trackDecisionWithMixpanel(layer, experiment, variation, isLayerHoldback)
    ['catch'](function(e) {
      Logger.warn('Tracker for', VENDOR, 'failed:', e);
    });
};

/**
 * Get Mixpanel API and track decision
 * @param {Layer} layer
 * @param {Experiment} experiment
 * @param {Variation} variation
 * @param {boolean} isLayerHoldback
 * @return {Promise} Resolves when Mixpanel API tracking is successful, or rejects if not
 * @private
 */
function trackDecisionWithMixpanel(layer:Layer, experiment:Experiment, variation:Variation, isLayerHoldback:boolean): Promise<any> {
  return getMixpanelAPI()
    .then(function track(mixpanelAPI) {
      Logger.log('Mixpanel variable found on window');

      updateReferrerIfNecessary(mixpanelAPI);

      var formattedNamesAndIds = generateAnalyticsValue.formatNamesAndIdsForAnalytics(layer, experiment, variation, false);
      // TODO: Add ability to track if user is bucketed into multiple experiments
      // within same campaign (WEB-1283)
      var propertyValue = generateLayerDecisionObject(
        formattedNamesAndIds.names, formattedNamesAndIds.idStrings, isLayerHoldback, MAXIMUM_PROPERTY_LENGTH, layer.policy);

      registerPropertyValue(mixpanelAPI, propertyValue);

      trackEvent(mixpanelAPI, layer.policy, formattedNamesAndIds);
    });
}

/**
 * @typedef {Object} MixpanelAPI
 * @property {Function} push Function which takes an array or function and applies the corresponding API
 */

/**
 * Poll for Mixpanel variable to appear
 * @returns {Promise} Resolves to {@see module:plugins/tracker_mixpanel~MixpanelAPI} object if found, or rejects on timeout
 * @private
 */
function getMixpanelAPI(): Promise<any> {
  var MAX_RETRIES = 50,
      WAIT_TIME = __TEST__ ? 5 : 200;

  return Poll.pollFor(function isReadyToTrack() {
    return WindowEnv.getGlobal(MIXPANEL_VARIABLE);
  }, WAIT_TIME, MAX_RETRIES);
}

/**
 * If there is effectiveExperimentAndVariation then user was redirected from another page/experiment.
 * We need to use that page's referrer for correct tracking.
 * https://mixpanel.com/help/questions/articles/what-properties-do-mixpanels-libraries-store-by-default
 * @param {module:plugins/tracker_mixpanel~MixpanelAPI} mixpanelAPI
 * @private
 */
function updateReferrerIfNecessary(mixpanelAPI:import('plugins/tracker_mixpanel/index').MixpanelAPI) {
  var effectiveExperimentAndVariation = RedirectUtil.getExperimentAndVariation();
  var effectiveReferrer = RedirectUtil.getReferrer();
  // TODO: WHy not check `if (_.isString(effectiveReferrer))` here and `if (effectiveReferrer)` two lines below?
  if (effectiveExperimentAndVariation) {
    var effectiveReferrerDomain;
    if (effectiveReferrer) {
      Logger.log('effectiveReferrer found, updating Mixpanel $referrer to:', effectiveReferrer);
      // First we update $referrer, which is defined as the referring URL, including your own domain
      effectiveReferrerDomain = urlUtil.guessDomain(effectiveReferrer, true);
      // Docs for register: https://mixpanel.com/help/reference/javascript-full-api-reference#mixpanel.register
      mixpanelAPI.push(['register', {
        '$referrer': effectiveReferrer,
        '$referring_domain': effectiveReferrerDomain,
      }]);

    }

    // Next we attempt to set $initial_referrer, defined as the referring URL at first arrival
    // Note: the mixpanel config must also have track_pageview set to false. Otherwise, the pageview event will occur
    // before we can properly set the initial referrer, and once it is set it is not possible to update it.
    mixpanelAPI.push(function() {
      // the 'this' context provided to this function is the mixpanel API object
      setInitialReferrer.call(this, effectiveReferrer, effectiveReferrerDomain);
    });
  }
}

/**
 * Register the super property containing campaign info
 * Docs for register: https://mixpanel.com/help/reference/javascript-full-api-reference#mixpanel.register
 * @param {module:plugins/tracker_mixpanel~MixpanelAPI} mixpanelAPI
 * @param {Object} propertyValue
 * @private
 */
function registerPropertyValue(mixpanelAPI:import('plugins/tracker_mixpanel/index').MixpanelAPI, propertyValue:any) {
  mixpanelAPI.push(['register', propertyValue]);
  Logger.log('Mixpanel super property registered with:', propertyValue);
}

/**
 * Track the decision event.
 *
 * If the layer policy is single experiment, the event name is formatted as
 * 'Experiment Decision: <experiment_name> (<experiment_id>)'
 * If the layer policy is not single experiment, the event name is formatted as
 * 'Campaign Decision: <campaign_name> (<campaign_id>)'
 * @param {module:plugins/tracker_mixpanel~MixpanelAPI} mixpanelAPI
 * @param {string} layerPolicy
 * @param {module:plugins/helpers/generate_analytics_value~FormattedNamesAndIds} formattedNamesAndIds
 * @private
 */
function trackEvent(mixpanelAPI:import('plugins/tracker_mixpanel/index').MixpanelAPI, layerPolicy:string, formattedNamesAndIds:import('core/plugins/helpers/generate_analytics_value').FormattedNamesAndIds) {
  var eventName;
  var eventModifier;
  var eventModifierMaxLength;
  if (Policy.isSingleExperimentPolicy(layerPolicy)) {
    eventModifierMaxLength = MAXIMUM_PROPERTY_LENGTH - EXPERIMENT_EVENT.length;
    eventModifier = combineAndTruncateIdAndName(formattedNamesAndIds.names.experiment, formattedNamesAndIds.idStrings.experiment, eventModifierMaxLength);
    eventName = EXPERIMENT_EVENT + eventModifier;
  } else {
    eventModifierMaxLength = MAXIMUM_PROPERTY_LENGTH - CAMPAIGN_EVENT.length;
    eventModifier = combineAndTruncateIdAndName(formattedNamesAndIds.names.layer, formattedNamesAndIds.idStrings.layer, eventModifierMaxLength);
    eventName = CAMPAIGN_EVENT + eventModifier;
  }
  mixpanelAPI.push(['track', eventName]);
  Logger.log('Optimizely Campaign Decision tracked in Mixpanel');
}

/**
 * @typedef {Object.<string>} LayerDecisionObject
 *
 * An object representing information about the layer/experiment. Contains the
 * following properties:
 *
 * [layerString]: [experimentString]
 * [experimentString]: [variationString] or 'holdback'
 * 'HB [layerString]': {boolean}
 */

/**
 * Generate an object containing layer data for analytics integrations.
 * Example outputs:
 * For a non single experiment layer:
 *  {
 *    '<layer_name> (<layer_id>)': '<exp_name> (<exp_id>)',
 *    '<exp_name> (<exp_id>)': '<variation_name> (<variation_id>)',
 *    'HB [<layer_name> (<layer_id>)]': 'false',
 *  }
 * For a single experiment layer:
 *  {
 *    '<exp_name> (<exp_id>)': '<variation_name> (<variation_id>)',
 *  }
 *
 * The keys and values are truncated to keep them each <= maxLength.
 * If a layer or variation property does not have a name associated with it,
 * only the id is used.
 * If an experiment does not have a name associated with it, the names of the
 * audiences on the experiment are concatenated together.
 *
 * @param {object} names Preformatted names for layer, experiment, and variation
 * @param {object} idStrings Preformatted id strings for layer, experiment, and variation
 * @param {boolean} isHoldback Whether or not the visitor is in the holdback
 * @param {number} maxLength the maximum length of the string to return
 * @param {string} layerPolicy Policy of the campaign. Indicates whether the campaign represents a single experiment.
 * @returns {LayerDecisionObject}
 * @private
 */
function generateLayerDecisionObject(names:any, idStrings:any, isHoldback:boolean, maxLength:number, layerPolicy:string): import('plugins/tracker_mixpanel/index').LayerDecisionObject {
  var maxLengthOfHoldbackLayerKey = maxLength - HOLDBACK_INITIALS.length - 2;
  var nameStrings = {} as any;
  _.each(names, function(name, key) {
    var idString = idStrings[key];
    var nameStringMaxLength = key === 'layer' ? maxLengthOfHoldbackLayerKey : maxLength;
    nameStrings[key] = combineAndTruncateIdAndName(name, idString, nameStringMaxLength);
  });

  var output = {} as any;

  output[nameStrings.experiment] = nameStrings.variation;

  if (!Policy.isSingleExperimentPolicy(layerPolicy)) {
    output[nameStrings.layer] = nameStrings.experiment;

    // We are intentionally conflating variation0 (holdback) and variation1 (holdback) here to provide parity with the
    // Optimizely X results page. This enables comparison between
    //   variation0 vs variation1 vs holdback
    // rather than
    //   variation0|treatment vs variation0|holdback vs variation1|treatment vs variation1|holdback
    output[nameStrings.experiment] = isHoldback ? HOLDBACK_STRING : nameStrings.variation;

    // Format holdback key. Holdback key is formatted as the following if:
    // Layer name is '': 'HB [(<layer_id>)]'
    // Layer name exists and maxLength is longer than layer name: 'HB [<layer_name> (<layer_id>)]'
    // Layer name exists and maxLength is shorter than layer name: 'HB[<layer_na>(<layer_id>)]'
    nameStrings.holdback = HOLDBACK_INITIALS + ' [' + nameStrings.layer + ']';
    output[nameStrings.holdback] = isHoldback;
  }

  return output;
}

/*
 * Helper function to concatenate entity name with its id, truncating name if necessary
 *
 * @param {string} name
 * @param {string} idString
 * @param {number} maxLength the maximum length of the string to return
 * @returns {string}
 * @private
 */
function combineAndTruncateIdAndName(name, idString, maxLength) {
  var combinedNameAndIdString;
  if (name === DEFAULT_STR) {
    combinedNameAndIdString = idString;
  } else {
    var lengthAvailableForName = maxLength - idString.length;
    if (name.length > lengthAvailableForName) {
      var nameOutputSize = Math.min(name.length, lengthAvailableForName);
      name = name.substring(0, nameOutputSize);
      // If the name has been truncated, don't include a space between the
      // name and id to save space
      combinedNameAndIdString = name + idString;
    } else {
      combinedNameAndIdString = name + ' ' + idString;
    }
  }
  return combinedNameAndIdString;
}

/*
 * Set $initial_referrer and $initial_reffering_domain super properties
 * If these properties have not been set, they will be set to the effectiveReferrer
 * and effectiveReferrerDomain. If they have already been set, this function will
 * not update them
 * Note: $direct is an undocumented value that Mixpanel uses internally for direct traffic
 * sources. According to Mixpanel support as of Mar 2017, this is unlikely to change
 *
 * @param {string} effectiveReferrer
 * @param {string} effectiveReferrerDomain
 * @this {module:plugins/tracker_mixpanel~MixpanelAPI}
 * @private
 */
function setInitialReferrer(effectiveReferrer, effectiveReferrerDomain) {
  // the 'this' context provided to this function is the mixpanel API object
  // Docs for get_config: https://mixpanel.com/help/reference/javascript-full-api-reference#mixpanel.get_config
  if (this.get_config('save_referrer')) {
    Logger.log('Calling register_once to potentially update $initial_referrer to:', effectiveReferrer);
    // Docs for register_once: https://mixpanel.com/help/reference/javascript-full-api-reference#mixpanel.register_once
    this.register_once({
      '$initial_referrer': effectiveReferrer || '$direct',
      '$initial_referring_domain': effectiveReferrerDomain || '$direct',
    });
  }
}

var trackerMixpanel = {
  preRedirectPolicy: trackingPolicies.PreRedirectPolicies.PERSIST_BEFORE_REDIRECT,
  postRedirectPolicy: trackingPolicies.PostRedirectPolicies.TRACK_IMMEDIATELY,
  nonRedirectPolicy: trackingPolicies.NonRedirectPolicies.TRACK_IMMEDIATELY,
  trackLayerDecision: trackLayerDecisionMixpanel,
  serializeSettings: JSONEnv.stringify,
  deserializeSettings: JSONEnv.parse,

};

module.exports = function(PluginManager) {
  PluginManager.registerAnalyticsTracker('mixpanel', trackerMixpanel);
};

if (__TEST__) {
  module.exports.plugin = trackerMixpanel;
  // expose for testing
  module.exports.generateLayerDecisionObject = generateLayerDecisionObject;
  module.exports.setInitialReferrer = setInitialReferrer;
}
